import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useProductTemplates, PromptTemplate } from "@/hooks/useProductTemplates";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, SortAsc, Clock, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TemplateItem from "./TemplateItem";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import LoadingDebugger from "../debugger/LoadingDebugger";

interface ProductTemplateSelectorProps {
  onPromptSelect: (promptId: string) => void;
  selectedPrompt: string | null;
  filterType?: "recent" | "favorites" | "search";
  categoryFilter?: string;
  displayType?: "sidebar" | "grid";
  maxItems?: number;
}

const ProductTemplateSelector = ({ 
  onPromptSelect, 
  selectedPrompt,
  filterType = "recent",
  categoryFilter,
  displayType = "sidebar",
  maxItems = 30
}: ProductTemplateSelectorProps) => {
  const { user } = useAuth();
  const [sortBy, setSortBy] = useState<"popular" | "recent" | "alphabetical">("popular");
  const { templates, isLoading, error, refetch } = useProductTemplates({ 
    categoryFilter, 
    sortBy, 
    limit: maxItems 
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTemplates, setFilteredTemplates] = useState<PromptTemplate[]>([]);
  const [favoriteTemplates, setFavoriteTemplates] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [categories, setCategories] = useState<string[]>([]);
  
  useEffect(() => {
    console.log(`ProductTemplateSelector rendered - filterType: ${filterType}, categoryFilter: ${categoryFilter}`);
    console.log(`Templates loading state: ${isLoading}, count: ${templates.length}`);
    if (error) {
      console.error("Template loading error:", error);
    }
  }, [filterType, categoryFilter, isLoading, templates.length, error]);
  
  useEffect(() => {
    if (user && filterType === "favorites") {
      fetchFavoriteTemplates();
    }
  }, [user, filterType]);
  
  useEffect(() => {
    if (templates.length > 0) {
      const uniqueCategories = Array.from(new Set(templates.map(t => t.category)));
      setCategories(uniqueCategories);
    }
  }, [templates]);
  
  useEffect(() => {
    let filtered = [...templates];
    
    if (searchTerm) {
      filtered = filtered.filter(
        template => 
          template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          template.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory) {
      filtered = filtered.filter(template => 
        template.category === selectedCategory
      );
    }
    
    if (filterType === "favorites" && favoriteTemplates.length > 0) {
      filtered = filtered.filter(template => 
        favoriteTemplates.includes(template.id)
      );
    }
    
    console.log(`Filtered templates: ${filtered.length} (from ${templates.length})`);
    setFilteredTemplates(filtered);
  }, [templates, searchTerm, filterType, favoriteTemplates, selectedCategory]);
  
  const fetchFavoriteTemplates = async () => {
    if (!user?.id) return;
    
    try {
      console.log("Fetching favorite templates for user:", user.id);
      const { data, error } = await supabase
        .from('user_saved_prompts')
        .select('prompt_id')
        .eq('user_id', user.id);
        
      if (error) {
        console.error("Error fetching favorites:", error);
        throw error;
      }
      
      const favoriteIds = data.map(item => item.prompt_id);
      console.log(`Found ${favoriteIds.length} favorites for user`);
      setFavoriteTemplates(favoriteIds);
    } catch (error) {
      console.error("Error fetching favorite templates:", error);
    }
  };
  
  const handleCategoryClick = (category: string) => {
    if (selectedCategory === category) {
      setSelectedCategory("");
    } else {
      setSelectedCategory(category);
    }
  };
  
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
  };

  const handleSortChange = (value: "popular" | "recent" | "alphabetical") => {
    setSortBy(value);
  };

  const handleRefresh = () => {
    console.log("Manually refreshing templates");
    refetch();
  };
  
  if (isLoading) {
    return displayType === "grid" ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-xl border border-border/40 p-4 h-64">
            <div className="flex items-start space-x-3 mb-4">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="flex-1">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4 mb-8" />
            <Skeleton className="h-9 w-full mt-auto" />
          </div>
        ))}
      </div>
    ) : (
      <TemplateLoadingSkeleton />
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Failed to load templates.</p>
        <p className="text-sm mt-2">{error}</p>
        <Button onClick={handleRefresh} variant="outline" className="mt-4">
          Try Again
        </Button>
        <LoadingDebugger 
          id={`template-selector-${categoryFilter || 'all'}`} 
          isLoading={isLoading} 
          dataCount={templates.length} 
          error={error}
        />
      </div>
    );
  }
  
  if (filterType === "favorites" && filteredTemplates.length === 0 && !searchTerm) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No favorite templates found</p>
        <p className="text-sm mt-2">Save templates by clicking the bookmark icon during a chat.</p>
        <LoadingDebugger 
          id={`template-selector-${categoryFilter || 'all'}`} 
          isLoading={isLoading} 
          dataCount={templates.length} 
          error={error}
        />
      </div>
    );
  }
  
  if (filteredTemplates.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          {searchTerm ? "No templates match your search" : "No templates available"}
        </p>
        <Button onClick={handleRefresh} variant="outline" className="mt-4">
          Refresh
        </Button>
        <LoadingDebugger 
          id={`template-selector-${categoryFilter || 'all'}`} 
          isLoading={isLoading} 
          dataCount={templates.length} 
          error={error}
        />
      </div>
    );
  }
  
  if (displayType === "grid") {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search templates..."
              className="pl-8 pr-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchTerm("")}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-2">
                <SortAsc className="h-4 w-4 mr-2" />
                Sort
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => handleSortChange("popular")}
                className={sortBy === "popular" ? "bg-muted" : ""}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Most Popular
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleSortChange("recent")}
                className={sortBy === "recent" ? "bg-muted" : ""}
              >
                <Clock className="h-4 w-4 mr-2" />
                Most Recent
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleSortChange("alphabetical")}
                className={sortBy === "alphabetical" ? "bg-muted" : ""}
              >
                <SortAsc className="h-4 w-4 mr-2" />
                Alphabetical
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {categories.length > 1 && (
          <ScrollArea className="max-h-20">
            <div className="flex flex-wrap gap-1.5 pb-2">
              {categories.map((category) => (
                <Badge
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => handleCategoryClick(category)}
                >
                  {category}
                </Badge>
              ))}
            </div>
          </ScrollArea>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <TemplateItem
              key={template.id}
              id={template.id}
              title={template.title}
              description={template.description}
              icon={template.icon}
              category={template.category}
              estimated_time={template.estimated_time}
              popularity={template.popularity}
              isSelected={selectedPrompt === template.id}
              onSelect={onPromptSelect}
              displayType="card"
            />
          ))}
        </div>
        
        <LoadingDebugger 
          id={`template-selector-${categoryFilter || 'all'}`} 
          isLoading={isLoading} 
          dataCount={templates.length} 
          error={error}
        />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {(filterType === "search") && (
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search templates..."
            className="pl-8 pr-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {(searchTerm || selectedCategory) && (
            <button 
              className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
              onClick={clearFilters}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      
      <div className="space-y-3">
        {filteredTemplates.map((template) => (
          <TemplateItem
            key={template.id}
            id={template.id}
            title={template.title}
            description={template.description}
            icon={template.icon}
            category={template.category}
            estimated_time={template.estimated_time}
            popularity={template.popularity}
            isSelected={selectedPrompt === template.id}
            onSelect={onPromptSelect}
          />
        ))}
      </div>
      
      <LoadingDebugger 
        id={`template-selector-${categoryFilter || 'all'}`} 
        isLoading={isLoading} 
        dataCount={templates.length} 
        error={error}
      />
    </div>
  );
};

const TemplateLoadingSkeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="flex items-start space-x-3">
        <Skeleton className="h-6 w-6 rounded" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      </div>
    ))}
  </div>
);

export default ProductTemplateSelector;
